import { useState, type FormEvent } from 'react';

const FORM_ID = 'dahs47boh653ivfvo4i0';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahs47boh653ivfvo4i0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    payload.append('form_id', FORM_ID);
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value);
      if (!text.trim()) return;
      payload.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试，或直接发送邮件联系我们。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功。请检查网络后重试，或直接发送邮件联系我们。');
    }
  };

  return (
    <section
      id="contact"
      className="scroll-mt-20 border-b border-background-200 bg-background-50 py-14 md:scroll-mt-24 md:py-20"
    >
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600 md:text-sm">
              <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
              Contact
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              入口推荐与纠错反馈
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              发现好用的入口、遇到地址失效、或者觉得分类可以更合理，都可以在这里告诉我们。
              信息填得越具体，核验和上线速度越快。
            </p>

            <ul className="mt-6 flex flex-col gap-3">
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className="ri-mail-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">反馈邮箱</p>
                  <p className="mt-0.5 text-sm text-foreground-600">feedback@heiliaodh.com</p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700">
                  <i className="ri-time-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">处理时效</p>
                  <p className="mt-0.5 text-sm text-foreground-600">多数反馈会在 1 个工作日内完成核验</p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                  <i className="ri-bookmark-3-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">收录说明</p>
                  <p className="mt-0.5 text-sm text-foreground-600">免费收录，不接受付费置顶</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              id="entry-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label htmlFor="fb-name" className="text-sm font-medium text-foreground-800">
                    入口 / 站点名称 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="fb-name"
                    name="entry_name"
                    type="text"
                    required
                    placeholder="例如：瓜田情报站"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="fb-type" className="text-sm font-medium text-foreground-800">
                    反馈类型 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="fb-type"
                    name="feedback_type"
                    required
                    defaultValue=""
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                  >
                    <option value="" disabled>
                      请选择反馈类型
                    </option>
                    <option value="推荐新入口">推荐新入口</option>
                    <option value="地址失效纠错">地址失效纠错</option>
                    <option value="分类建议">分类建议</option>
                    <option value="其他反馈">其他反馈</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="fb-addr" className="text-sm font-medium text-foreground-800">
                    入口地址
                  </label>
                  <input
                    id="fb-addr"
                    name="entry_address"
                    type="text"
                    placeholder="粘贴你知道的入口地址（选填）"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="fb-email" className="text-sm font-medium text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    placeholder="便于我们回复处理结果（选填）"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <label htmlFor="fb-message" className="text-sm font-medium text-foreground-800">
                  补充说明 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="简单描述一下这个入口适合什么人群、或者具体遇到了什么问题（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                ></textarea>
              </div>

              <input
                className="form-guard-alt"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === 'error' && formError && (
                <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true"></i>
                  {formError}
                </p>
              )}

              {status === 'success' && (
                <p className="mt-4 flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base" aria-hidden="true"></i>
                  提交成功，感谢你的分享！我们会尽快核验并在列表中更新这个入口。
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} aria-hidden="true"></i>
                {status === 'submitting' ? '提交中…' : '提交反馈'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}